import {
  GetCarInspection,
  UpdateCarInspection,
} from "api/services/car";
import React, { useEffect, useRef, useState } from "react";
import {
  CarInspectionCheckType,
  CarInspectionDefectType,
  CarInspectionStatusType,
  InspectionCheckStatusType,
  InspectionDefectSeverityType,
} from "types/loadedData";
import { compressCaptureImage } from "utils/captureImages";
import { dashboardConfirm } from "i18n/dashboardLanguage";
import "./InspectionEditor.css";

type InspectionPhoto = {
  id: string;
  url: string;
  file?: File;
};

type EditableDefect = Omit<CarInspectionDefectType, "images"> & {
  clientId: string;
  images: InspectionPhoto[];
};

const MAX_EVIDENCE_PHOTOS = 6;
const MAX_SOURCE_IMAGE_SIZE = 50 * 1024 * 1024;
const SUPPORTED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/pjpeg",
  "image/png",
  "image/x-png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/avif",
]);
const SUPPORTED_IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "jfif",
  "png",
  "webp",
  "heic",
  "heif",
  "avif",
]);

const isSupportedEvidenceImage = (file: File) => {
  const type = String(file.type || "").toLowerCase();
  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  return SUPPORTED_IMAGE_TYPES.has(type) || SUPPORTED_IMAGE_EXTENSIONS.has(extension);
};

const prepareEvidenceImage = async (file: File) => {
  try {
    const compressed = await compressCaptureImage(file);
    const baseName = file.name.replace(/\.[^.]+$/, "") || "evidence";
    return new File([compressed], `${baseName}-evidence.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    // HEIC/HEIF decoding is not available in every browser. The backend and
    // Cloudinary can still process the original file.
    return file;
  }
};

const CHECKS = [
  ["documents", "Documents"],
  ["chassis", "Chassis / VIN"],
  ["exterior", "Exterior"],
  ["interior", "Interior"],
  ["engine", "Engine"],
  ["transmission", "Transmission"],
  ["brakes", "Brakes"],
  ["suspension", "Suspension"],
  ["tires", "Tires"],
  ["electronics", "Electronics"],
  ["ac", "Air conditioning"],
  ["testDrive", "Test drive"],
  ["odometer", "Odometer"],
] as const;

const makeId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const emptyChecks = (): CarInspectionCheckType[] =>
  CHECKS.map(([key]) => ({ key, status: "NotChecked", note: "" }));

const hydrateChecks = (checks?: CarInspectionCheckType[]) => {
  const byKey = new Map((checks || []).map((check) => [check.key, check]));
  return CHECKS.map(([key]) => ({
    key,
    status: byKey.get(key)?.status || "NotChecked",
    note: byKey.get(key)?.note || "",
  })) as CarInspectionCheckType[];
};

const hydrateDefects = (defects?: CarInspectionDefectType[]): EditableDefect[] =>
  (defects || []).map((defect) => ({
    ...defect,
    clientId: defect._id || makeId(),
    images: (defect.images || []).map((url) => ({ id: makeId(), url })),
  }));

const formatDate = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("en", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(value))
    : "";

function InspectionEditor({ carId }: { carId: string }) {
  const [status, setStatus] =
    useState<CarInspectionStatusType>("NotInspected");
  const [summary, setSummary] = useState("");
  const [checks, setChecks] = useState<CarInspectionCheckType[]>(emptyChecks);
  const [defects, setDefects] = useState<EditableDefect[]>([]);
  const [inspectedAt, setInspectedAt] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [inspectorName, setInspectorName] = useState("");
  const [invalidatedReason, setInvalidatedReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const objectUrls = useRef(new Set<string>());

  const applyInspection = (inspection: any) => {
    setStatus(inspection?.inspectionStatus || "NotInspected");
    setSummary(inspection?.inspectionReport?.summary || "");
    setChecks(hydrateChecks(inspection?.inspectionReport?.checks));
    setDefects(hydrateDefects(inspection?.inspectionReport?.defects));
    setInspectedAt(inspection?.inspectedAt || "");
    setValidUntil(inspection?.inspectionValidUntil || "");
    setInspectorName(inspection?.inspectedBy?.name || "");
    setInvalidatedReason(inspection?.inspectionInvalidationReason || "");
  };

  useEffect(() => {
    let active = true;
    const createdObjectUrls = objectUrls.current;
    const load = async () => {
      setError("");
      const response = await GetCarInspection({ id: carId, setLoading });
      if (!active) return;
      if (response.status === 200 && response.inspection) {
        applyInspection(response.inspection);
      } else {
        setError(response.error || "Inspection could not be loaded.");
      }
    };
    void load();
    return () => {
      active = false;
      createdObjectUrls.forEach((url) => URL.revokeObjectURL(url));
      createdObjectUrls.clear();
    };
  }, [carId]);

  const updateCheck = (
    key: string,
    field: "status" | "note",
    value: string
  ) => {
    setChecks((current) =>
      current.map((check) =>
        check.key === key ? { ...check, [field]: value } : check
      )
    );
  };

  const addDefect = () => {
    setDefects((current) => [
      ...current,
      {
        clientId: makeId(),
        area: "",
        severity: "Minor",
        note: "",
        images: [],
      },
    ]);
  };

  const updateDefect = (clientId: string, field: string, value: any) => {
    setDefects((current) =>
      current.map((defect) =>
        defect.clientId === clientId ? { ...defect, [field]: value } : defect
      )
    );
  };

  const addDefectPhotos = async (clientId: string, files: FileList | null) => {
    if (!files?.length) return;
    setError("");
    setMessage("");

    const target = defects.find((defect) => defect.clientId === clientId);
    const available = Math.max(
      0,
      MAX_EVIDENCE_PHOTOS - (target?.images.length || 0)
    );
    if (available === 0) {
      setError(`A defect can have up to ${MAX_EVIDENCE_PHOTOS} evidence photos.`);
      return;
    }

    const selected = Array.from(files);
    const supported = selected.filter(
      (file) =>
        isSupportedEvidenceImage(file) && file.size <= MAX_SOURCE_IMAGE_SIZE
    );
    if (supported.length === 0) {
      setError("Choose a JPG, PNG, WEBP, HEIC, HEIF, or AVIF image under 50 MB.");
      return;
    }

    const prepared = await Promise.all(
      supported.slice(0, available).map(prepareEvidenceImage)
    );
    const newPhotos = prepared.map((file) => {
      const url = URL.createObjectURL(file);
      objectUrls.current.add(url);
      return { id: makeId(), url, file };
    });

    setDefects((current) =>
      current.map((defect) =>
        defect.clientId === clientId
          ? { ...defect, images: [...defect.images, ...newPhotos] }
          : defect
      )
    );

    if (supported.length < selected.length) {
      setError("Some files were skipped because their type or size is unsupported.");
    } else if (selected.length > available) {
      setError(`Only ${available} more evidence photo${available === 1 ? "" : "s"} can be added.`);
    }
  };

  const removeDefectPhoto = (clientId: string, photo: InspectionPhoto) => {
    if (photo.file) {
      URL.revokeObjectURL(photo.url);
      objectUrls.current.delete(photo.url);
    }
    setDefects((current) =>
      current.map((defect) =>
        defect.clientId === clientId
          ? {
              ...defect,
              images: defect.images.filter((image) => image.id !== photo.id),
            }
          : defect
      )
    );
  };

  const removeDefect = (clientId: string) => {
    const defect = defects.find((item) => item.clientId === clientId);
    defect?.images.forEach((photo) => {
      if (!photo.file) return;
      URL.revokeObjectURL(photo.url);
      objectUrls.current.delete(photo.url);
    });
    setDefects((current) =>
      current.filter((item) => item.clientId !== clientId)
    );
  };

  const handleSave = async () => {
    if (
      status === "NotInspected" &&
      !dashboardConfirm("Remove the BossCar inspection and all defect evidence?")
    ) {
      return;
    }

    setError("");
    setMessage("");
    const payload = new FormData();
    const files: File[] = [];
    const serializedDefects = defects.map((defect) => ({
      ...(defect._id ? { _id: defect._id } : {}),
      area: defect.area,
      severity: defect.severity,
      note: defect.note,
      estimatedRepairCost: defect.estimatedRepairCost,
      images: defect.images.map((photo) => {
        if (!photo.file) return photo.url;
        const index = files.push(photo.file) - 1;
        return `upload:${index}`;
      }),
    }));

    payload.append("inspectionStatus", status);
    payload.append("inspectionSummary", summary);
    payload.append("inspectionChecks", JSON.stringify(checks));
    payload.append("inspectionDefects", JSON.stringify(serializedDefects));
    files.forEach((file) =>
      payload.append("inspectionImages", file, file.name || "evidence.jpg")
    );

    const response = await UpdateCarInspection({
      id: carId,
      payload,
      setLoading: setSaving,
    });
    if (response.status === 200 && response.inspection) {
      objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.current.clear();
      applyInspection(response.inspection);
      setMessage("Inspection saved. Buyer trust details are now up to date.");
    } else {
      setError(response.error || "Inspection could not be saved.");
    }
  };

  if (loading) {
    return <section className="inspection-editor inspection-editor-loading">Loading inspection...</section>;
  }

  return (
    <section className="inspection-editor">
      <div className="inspection-editor-header">
        <div>
          <p className="inspection-kicker">USED CAR TRUST</p>
          <h2>BossCar inspection</h2>
          <p className="inspection-subtitle">
            Record what was checked and show every known defect clearly.
          </p>
        </div>
        <select
          className={`inspection-status inspection-status-${status.toLowerCase()}`}
          value={status}
          onChange={(event) =>
            setStatus(event.target.value as CarInspectionStatusType)
          }
        >
          <option value="NotInspected">Not inspected</option>
          <option value="Passed">Inspected</option>
          <option value="Failed">Did not pass</option>
        </select>
      </div>

      {(inspectedAt || invalidatedReason) && (
        <div className="inspection-history">
          {inspectedAt && (
            <span>
              Last checked {formatDate(inspectedAt)}
              {inspectorName ? ` by ${inspectorName}` : ""}
            </span>
          )}
          {validUntil && <span>Valid until {formatDate(validUntil)}</span>}
          {invalidatedReason && (
            <strong>Badge removed: {invalidatedReason}</strong>
          )}
        </div>
      )}

      <label className="inspection-field">
        <span>Inspector summary</span>
        <textarea
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          placeholder="Example: Clean daily-use car. Two minor body defects are disclosed below."
          maxLength={1500}
        />
      </label>

      <div className="inspection-section-heading">
        <div>
          <h3>Inspection checklist</h3>
          <p>Every item must be checked before the car receives the badge.</p>
        </div>
        <span>
          {checks.filter((check) => check.status !== "NotChecked").length}/
          {CHECKS.length} checked
        </span>
      </div>

      <div className="inspection-check-grid">
        {CHECKS.map(([key, label]) => {
          const check = checks.find((item) => item.key === key)!;
          return (
            <div className="inspection-check" key={key}>
              <div className="inspection-check-top">
                <strong>{label}</strong>
                <select
                  value={check.status}
                  onChange={(event) =>
                    updateCheck(
                      key,
                      "status",
                      event.target.value as InspectionCheckStatusType
                    )
                  }
                >
                  <option value="NotChecked">Not checked</option>
                  <option value="Good">Good</option>
                  <option value="Attention">Needs attention</option>
                </select>
              </div>
              <input
                value={check.note || ""}
                onChange={(event) => updateCheck(key, "note", event.target.value)}
                placeholder="Optional factual note"
                maxLength={500}
              />
            </div>
          );
        })}
      </div>

      <div className="inspection-section-heading inspection-defects-heading">
        <div>
          <h3>Known defects</h3>
          <p>Add a wide photo and a close-up whenever possible.</p>
        </div>
        <button type="button" className="inspection-add" onClick={addDefect}>
          + Add defect
        </button>
      </div>

      {defects.length === 0 ? (
        <div className="inspection-empty">No defects recorded.</div>
      ) : (
        <div className="inspection-defect-list">
          {defects.map((defect, index) => (
            <article className="inspection-defect" key={defect.clientId}>
              <div className="inspection-defect-title">
                <strong>Defect {index + 1}</strong>
                <button type="button" onClick={() => removeDefect(defect.clientId)}>
                  Remove
                </button>
              </div>
              <div className="inspection-defect-fields">
                <label>
                  <span>Area</span>
                  <input
                    value={defect.area}
                    onChange={(event) =>
                      updateDefect(defect.clientId, "area", event.target.value)
                    }
                    placeholder="Example: Rear bumper"
                  />
                </label>
                <label>
                  <span>Severity</span>
                  <select
                    value={defect.severity}
                    onChange={(event) =>
                      updateDefect(
                        defect.clientId,
                        "severity",
                        event.target.value as InspectionDefectSeverityType
                      )
                    }
                  >
                    <option value="Minor">Minor</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Major">Major</option>
                  </select>
                </label>
                <label>
                  <span>Estimated repair (Birr, optional)</span>
                  <input
                    type="number"
                    min="0"
                    value={defect.estimatedRepairCost ?? ""}
                    onChange={(event) =>
                      updateDefect(
                        defect.clientId,
                        "estimatedRepairCost",
                        event.target.value ? Number(event.target.value) : undefined
                      )
                    }
                    placeholder="0"
                  />
                </label>
              </div>
              <label className="inspection-field">
                <span>What did you observe?</span>
                <textarea
                  value={defect.note}
                  onChange={(event) =>
                    updateDefect(defect.clientId, "note", event.target.value)
                  }
                  placeholder="Describe only what was observed."
                  maxLength={1000}
                />
              </label>
              <div className="inspection-photo-grid">
                {defect.images.map((photo) => (
                  <div className="inspection-photo" key={photo.id}>
                    <img src={photo.url} alt={`${defect.area || "Defect"} evidence`} />
                    <button
                      type="button"
                      onClick={() => removeDefectPhoto(defect.clientId, photo)}
                      aria-label="Remove evidence photo"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {defect.images.length < MAX_EVIDENCE_PHOTOS && (
                  <label className="inspection-photo-add">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif,.jpg,.jpeg,.jfif,.png,.webp,.heic,.heif,.avif"
                      multiple
                      onChange={(event) => {
                        void addDefectPhotos(defect.clientId, event.target.files);
                        event.currentTarget.value = "";
                      }}
                    />
                    <span>+ Add photos</span>
                  </label>
                )}
              </div>
              <p className="inspection-photo-help">
                JPG, PNG, WEBP, HEIC or AVIF. Up to {MAX_EVIDENCE_PHOTOS} photos.
              </p>
            </article>
          ))}
        </div>
      )}

      {error && <p className="inspection-feedback inspection-error">{error}</p>}
      {message && (
        <p className="inspection-feedback inspection-success">{message}</p>
      )}
      <div className="inspection-save-row">
        <p>
          “Inspected” reports are public for 14 days. Major defects cannot pass.
        </p>
        <button type="button" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save inspection"}
        </button>
      </div>
    </section>
  );
}

export default InspectionEditor;
